import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Download from '@mui/icons-material/Download';
import Sync from '@mui/icons-material/Sync';
import HelpOutline from '@mui/icons-material/HelpOutline';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const emptySettings = {
  advertising_mode: 'internal',
  log_prefix: '',
  transfer_method: 'dropbox',
  dropbox_folder: '',
  dropbox_enabled: false,
  local_folder: '',
  ftp_host: '',
  ftp_port: 21,
  ftp_username: '',
  ftp_password: '',
  ftp_remote_path: '',
  poll_interval_minutes: 15,
  csv_export: {
    enabled: false,
    dropbox_folder: '',
    schedule: 'manual',
  },
  asrun_export: {
    enabled: false,
    schedule: 'manual',
    include_missed: true,
    format: 'csv',
  },
  expected_filename: 'dd-mm-hh-hh-breakno.m3u',
  example_filename: '',
  example_unprefixed_filename: '',
  pending_imports: 0,
  dropbox_connected: false,
  station_count: 1,
  unprefixed_allowed: true,
  prefix_now_required: false,
};

function statusColor(status) {
  if (status === 'applied' || status === 'matched' || status === 'ready') return 'success';
  if (status === 'needs_review' || status === 'suggested') return 'warning';
  if (status === 'rejected' || status === 'unmatched') return 'error';
  return 'default';
}

export default function ExternalAdvertisingSettings({
  stationId,
  stations = [],
  canManage = false,
  onStationChange,
}) {
  const [settings, setSettings] = useState(emptySettings);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imports, setImports] = useState([]);
  const [activeImport, setActiveImport] = useState(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [spotQuery, setSpotQuery] = useState('');
  const [spotOptions, setSpotOptions] = useState([]);
  const [matchingItemId, setMatchingItemId] = useState(null);
  const [asrunDate, setAsrunDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  });

  const selectedStation = useMemo(
    () => stations.find((station) => String(station.id) === String(stationId)) || null,
    [stations, stationId]
  );

  const loadSettings = useCallback(async () => {
    if (!stationId || !canManage) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/external-advertising/settings`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setSettings({
        ...emptySettings,
        ...res.data,
        ftp_password: '',
        csv_export: { ...emptySettings.csv_export, ...(res.data.csv_export || {}) },
        asrun_export: { ...emptySettings.asrun_export, ...(res.data.asrun_export || {}) },
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load external advertising settings' });
    } finally {
      setLoading(false);
    }
  }, [stationId, canManage]);

  const loadImports = useCallback(async () => {
    if (!stationId || !canManage) return;
    try {
      const res = await axios.get(`${API_URL}/api/external-advertising/imports`, {
        params: { station_id: stationId, limit: 25 },
        withCredentials: true,
      });
      setImports(res.data?.imports || []);
    } catch (error) {
      console.error('Failed to load external advertising imports', error);
    }
  }, [stationId, canManage]);

  useEffect(() => {
    loadSettings();
    loadImports();
  }, [loadSettings, loadImports]);

  useEffect(() => {
    if (!matchingItemId) return undefined;
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/external-advertising/spots`, {
          params: { station_id: stationId, search: spotQuery || undefined, limit: 25 },
          withCredentials: true,
        });
        setSpotOptions(res.data?.spots || []);
      } catch (_) {
        setSpotOptions([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [spotQuery, matchingItemId, stationId]);

  if (!canManage) return null;

  const updateCsv = (patch) => {
    setSettings((prev) => ({ ...prev, csv_export: { ...prev.csv_export, ...patch } }));
  };

  const updateAsRun = (patch) => {
    setSettings((prev) => ({ ...prev, asrun_export: { ...prev.asrun_export, ...patch } }));
  };

  const handleSave = async () => {
    if (!stationId) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        station_id: stationId,
        advertising_mode: settings.advertising_mode,
        external_advertising: {
          log_prefix: settings.log_prefix,
          transfer_method: settings.transfer_method,
          dropbox_folder: settings.dropbox_folder,
          dropbox_enabled: settings.dropbox_enabled,
          local_folder: settings.local_folder,
          ftp_host: settings.ftp_host,
          ftp_port: settings.ftp_port,
          ftp_username: settings.ftp_username,
          ftp_remote_path: settings.ftp_remote_path,
          poll_interval_minutes: settings.poll_interval_minutes,
          csv_export: settings.csv_export,
          asrun_export: settings.asrun_export,
        },
      };
      if (settings.ftp_password) payload.external_advertising.ftp_password = settings.ftp_password;
      const res = await axios.put(`${API_URL}/api/external-advertising/settings`, payload, { withCredentials: true });
      setSettings({
        ...emptySettings,
        ...res.data,
        ftp_password: '',
        csv_export: { ...emptySettings.csv_export, ...(res.data.csv_export || {}) },
        asrun_export: { ...emptySettings.asrun_export, ...(res.data.asrun_export || {}) },
      });
      setMessage({ type: 'success', text: 'External advertising settings saved' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !stationId) return;
    setBusy('upload');
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('station_id', stationId);
      const res = await axios.post(`${API_URL}/api/external-advertising/import`, form, { withCredentials: true });
      await loadImports();
      await loadSettings();
      if (res.data?.status === 'applied') {
        setMessage({ type: 'success', text: `Imported and applied ${file.name}` });
      } else {
        setActiveImport(res.data);
        setMessage({ type: 'warning', text: res.data?.warning_message || 'Import needs review before it can go on air.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to import traffic file' });
    } finally {
      setBusy('');
    }
  };

  const handleWatchImport = async () => {
    setBusy('watch');
    setMessage(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/external-advertising/import-watch`,
        { station_id: stationId, force: false },
        { withCredentials: true }
      );
      await loadImports();
      const count = res.data?.imported?.length || 0;
      const review = (res.data?.imported || []).find((item) => item.status === 'needs_review');
      if (review) setActiveImport(review);
      const prefixNeeded = (res.data?.skipped || []).filter((row) => row.reason === 'prefix_required').length;
      const method = res.data?.transfer_method === 'dropbox' ? 'Dropbox' : 'the traffic folder';
      let text = count ? `Imported ${count} break file(s) from ${method}.` : `No new break files were found in ${method}.`;
      if (prefixNeeded) {
        text += ` ${prefixNeeded} unprefixed file(s) were ignored. Rename them to ${settings.example_filename}.`;
      }
      setMessage({
        type: count ? 'success' : (prefixNeeded ? 'warning' : 'info'),
        text,
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Traffic folder import failed' });
    } finally {
      setBusy('');
    }
  };

  const handleExportDownload = async () => {
    setBusy('csv');
    try {
      const res = await axios.get(`${API_URL}/api/external-advertising/export.csv?station_id=${stationId}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.href = url;
      link.download = match?.[1] || 'spots.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'CSV download failed' });
    } finally {
      setBusy('');
    }
  };

  const handleExportWatch = async () => {
    setBusy('csv-watch');
    try {
      const res = await axios.post(
        `${API_URL}/api/external-advertising/export-watch`,
        { station_id: stationId },
        { withCredentials: true }
      );
      setMessage({ type: 'success', text: `Uploaded ${res.data.filename} (${res.data.spotCount} spots).` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Spots CSV export failed' });
    } finally {
      setBusy('');
    }
  };

  const downloadBlob = (data, filename, fallback) => {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || fallback;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAsRunDownload = async () => {
    setBusy('asrun');
    try {
      const params = new URLSearchParams({
        station_id: stationId,
        date: asrunDate,
        include_missed: settings.asrun_export.include_missed === false ? 'false' : 'true',
        format: settings.asrun_export.format || 'csv',
      });
      const format = settings.asrun_export.format || 'csv';
      const res = await axios.get(`${API_URL}/api/external-advertising/asrun.${format}?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      downloadBlob(res.data, match?.[1], `${asrunDate}-asrun.${format}`);
      setMessage({ type: 'success', text: `Downloaded as-run log for ${asrunDate}.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'As-run download failed' });
    } finally {
      setBusy('');
    }
  };

  const handleAsRunUpload = async () => {
    setBusy('asrun-watch');
    try {
      const res = await axios.post(
        `${API_URL}/api/external-advertising/export-asrun`,
        {
          station_id: stationId,
          date: asrunDate,
          include_missed: settings.asrun_export.include_missed !== false,
          format: settings.asrun_export.format || 'csv',
        },
        { withCredentials: true }
      );
      setMessage({
        type: 'success',
        text: `Uploaded ${res.data.filename} (${res.data.playedCount} played, ${res.data.missedCount} missed).`,
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'As-run upload failed' });
    } finally {
      setBusy('');
    }
  };

  const openImport = async (id) => {
    const res = await axios.get(`${API_URL}/api/external-advertising/imports/${id}`, { withCredentials: true });
    setActiveImport(res.data);
  };

  const patchItem = async (itemId, body) => {
    const res = await axios.put(
      `${API_URL}/api/external-advertising/imports/${activeImport.id}/items/${itemId}`,
      body,
      { withCredentials: true }
    );
    setActiveImport(res.data);
    loadImports();
  };

  const applyOrReject = async (action, extra = {}) => {
    const res = await axios.post(
      `${API_URL}/api/external-advertising/imports/${activeImport.id}/${action}`,
      extra,
      { withCredentials: true }
    );
    setActiveImport(res.data);
    loadImports();
    loadSettings();
    setMessage({
      type: action === 'reject' ? 'info' : 'success',
      text: action === 'reject' ? 'Import rejected.' : 'Break log applied.',
    });
  };

  const external = settings.advertising_mode === 'external';
  const multiStation = stations.length > 1;
  const method = settings.transfer_method || 'dropbox';

  return (
    <Card sx={{ bgcolor: 'var(--rs-paper)', border: '2px solid #a78bfa', mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#a78bfa' }}>
              External Traffic
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedStation
                ? `Per-station source for ${selectedStation.name} commercial spots. Default is RadioSuite traffic.`
                : 'Select a station to configure Acquira, Admaster, or another traffic system.'}
            </Typography>
          </Box>
          {settings.pending_imports > 0 && (
            <Chip color="warning" label={`${settings.pending_imports} break file(s) need matching`} />
          )}
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {settings.station_count > 1 && settings.unprefixed_allowed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You now have {settings.station_count} stations. Unprefixed break files such as{' '}
            <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_unprefixed_filename || '25-08-14-15-1.m3u'}</Box>
            {' '}still import for this station. Before you share this folder with another station or turn on its external traffic, rename files to{' '}
            <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box>.
          </Alert>
        )}
        {settings.prefix_now_required && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            More than one station is watching this traffic folder. Files without a station prefix are ignored.
            Rename them to <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box>.
          </Alert>
        )}

        {stations.length > 1 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Station</InputLabel>
            <Select
              value={stationId || ''}
              label="Station"
              onChange={(e) => onStationChange && onStationChange(e.target.value)}
            >
              {stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {!stationId ? (
          <Alert severity="info">Select a station above to configure internal or external traffic.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Traffic source</InputLabel>
              <Select
                value={settings.advertising_mode}
                label="Traffic source"
                onChange={(e) => setSettings((prev) => ({ ...prev, advertising_mode: e.target.value }))}
              >
                <MenuItem value="internal">Internal scheduler (campaigns and rules)</MenuItem>
                <MenuItem value="external">External traffic (Acquira, Admaster, M3U/CSV)</MenuItem>
              </Select>
            </FormControl>

            <Accordion sx={{ mb: 2, bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline fontSize="small" sx={{ color: '#a78bfa' }} />
                  <Typography variant="subtitle2">Setup guide for Acquira, Admaster, and similar tools</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  RadioSuite still plays the spots and fills commercial-break markers in the hour log.
                  The external product only decides which commercials go in each break.
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <strong>1. Export the spots library</strong> as CSV (CartID, Title, Client, Duration, Filename) and import it into Acquira, Admaster, or your traffic system.<br />
                  <strong>2. One file per break</strong> named <Box component="code" sx={{ fontFamily: 'monospace' }}>{settings.expected_filename}</Box>
                  {settings.prefix_now_required
                    ? ' — a station prefix is required because another station is watching the same folder.'
                    : settings.station_count > 1
                      ? ' — unprefixed names still work; add the prefix before sharing this folder with another station.'
                      : ' — a prefix is optional while this is the only station.'}<br />
                  <strong>3. Example for now:</strong> <Box component="code" sx={{ fontFamily: 'monospace' }}>{settings.example_filename}</Box> — day, month, start hour, end hour, then the break number (1–20) from Commercial Breaks.<br />
                  <strong>4. File contents</strong> can be M3U, text, or CSV. Cart numbers, filenames, and titles are matched to campaign spots.<br />
                  <strong>5. Dropbox is preferred.</strong> Connect it under Settings → Dropbox, then point this station at a traffic folder. Use a local folder, FTP, or SFTP if the traffic system cannot write to Dropbox.<br />
                  <strong>6. Matching</strong> uses cart number, filename, and title. Uncertain lines are held so you can match, skip, or reject the break.<br />
                  <strong>7. As-run / played log</strong> — after spots air, download or upload actual played times (cart, date, scheduled time, played time, status) as CSV, XML, or JSON so Acquira, Admaster, MusicMaster, or another CRM can reconcile billing. Daily upload at 05:20 sends yesterday’s file to the same folder as the spots CSV.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {external && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Internal traffic generation is disabled for this station. Import a separate file for each break named{' '}
                  <Box component="strong" sx={{ fontFamily: 'monospace' }}>{settings.expected_filename}</Box>.
                  {multiStation && ' Other stations can keep using RadioSuite traffic.'}
                </Alert>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Log file prefix"
                      value={settings.log_prefix}
                      onChange={(e) => setSettings((prev) => ({ ...prev, log_prefix: e.target.value }))}
                      helperText={settings.prefix_now_required
                        ? 'Required on every break filename now that another station is watching this folder.'
                        : 'Used when several stations share a folder. Unprefixed files still import until another station watches the same folder.'}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Expected filename"
                      value={settings.expected_filename}
                      InputProps={{ readOnly: true }}
                      helperText={`Example: ${settings.example_filename}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Transfer method</InputLabel>
                      <Select
                        value={method}
                        label="Transfer method"
                        onChange={(e) => {
                          const next = e.target.value;
                          setSettings((prev) => ({
                            ...prev,
                            transfer_method: next,
                            ftp_port: next === 'sftp' ? 22 : next === 'ftp' ? 21 : prev.ftp_port,
                          }));
                        }}
                      >
                        <MenuItem value="dropbox">Dropbox (preferred)</MenuItem>
                        <MenuItem value="local_folder">Local folder</MenuItem>
                        <MenuItem value="ftp">FTP</MenuItem>
                        <MenuItem value="sftp">SFTP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Poll interval (minutes)"
                      value={settings.poll_interval_minutes}
                      onChange={(e) => setSettings((prev) => ({ ...prev, poll_interval_minutes: e.target.value }))}
                    />
                  </Grid>
                  {method === 'dropbox' && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Dropbox traffic folder"
                        value={settings.dropbox_folder}
                        onChange={(e) => setSettings((prev) => ({ ...prev, dropbox_folder: e.target.value }))}
                        placeholder="/RadioSuite/Traffic/Station"
                        helperText={settings.dropbox_connected ? 'Dropbox is connected.' : 'Connect Dropbox in Settings first.'}
                      />
                    </Grid>
                  )}
                  {method === 'local_folder' && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="Local traffic folder"
                        value={settings.local_folder}
                        onChange={(e) => setSettings((prev) => ({ ...prev, local_folder: e.target.value }))}
                        placeholder="C:\\Traffic\\Station or /var/traffic/station"
                      />
                    </Grid>
                  )}
                  {(method === 'ftp' || method === 'sftp') && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Host"
                          value={settings.ftp_host}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_host: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Port"
                          value={settings.ftp_port}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_port: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Username"
                          value={settings.ftp_username}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_username: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="password"
                          label={settings.ftp_password_set ? 'Password (leave blank to keep)' : 'Password'}
                          value={settings.ftp_password}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_password: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Remote path"
                          value={settings.ftp_remote_path}
                          onChange={(e) => setSettings((prev) => ({ ...prev, ftp_remote_path: e.target.value }))}
                          placeholder="/traffic/logs"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={settings.dropbox_enabled}
                      onChange={(e) => setSettings((prev) => ({ ...prev, dropbox_enabled: e.target.checked }))}
                    />
                  }
                  label={method === 'dropbox' ? 'Watch Dropbox folder for new break files' : 'Watch this folder for new break files'}
                />

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mt: 1, mb: 1 }}>Spots CSV for traffic systems</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Automatic upload</InputLabel>
                      <Select
                        label="Automatic upload"
                        value={settings.csv_export.schedule}
                        onChange={(e) => updateCsv({ schedule: e.target.value })}
                      >
                        <MenuItem value="manual">Manual only</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly (Monday)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {method === 'dropbox' && (
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="CSV Dropbox folder"
                        value={settings.csv_export.dropbox_folder}
                        onChange={(e) => updateCsv({ dropbox_folder: e.target.value })}
                        placeholder={settings.dropbox_folder || '/RadioSuite/Traffic'}
                      />
                    </Grid>
                  )}
                </Grid>
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={settings.csv_export.enabled}
                      onChange={(e) => updateCsv({ enabled: e.target.checked })}
                    />
                  }
                  label="Enable scheduled spots CSV uploads"
                />

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mt: 1, mb: 1 }}>As-run / played log for traffic systems</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Actual air times from RadioSuite, including missed spots, for Acquira, Admaster, MusicMaster, or another CRM.
                  Choose CSV (typical for Acquira/Admaster), XML (MusicMaster/ENCO), or JSON (custom ingest).
                  Files are named <Box component="code" sx={{ fontFamily: 'monospace' }}>PREFIX-asrun-YYYYMMDD.csv</Box> (or .xml / .json)
                  and go to the same upload folder — not the inbound break-log folder.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Log date"
                      value={asrunDate}
                      onChange={(e) => setAsrunDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>File format</InputLabel>
                      <Select
                        label="File format"
                        value={settings.asrun_export?.format || 'csv'}
                        onChange={(e) => updateAsRun({ format: e.target.value })}
                      >
                        <MenuItem value="csv">CSV (Acquira, Admaster, G Selector)</MenuItem>
                        <MenuItem value="xml">XML (MusicMaster / ENCO)</MenuItem>
                        <MenuItem value="json">JSON</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Automatic as-run upload</InputLabel>
                      <Select
                        label="Automatic as-run upload"
                        value={settings.asrun_export.schedule}
                        onChange={(e) => updateAsRun({ schedule: e.target.value })}
                      >
                        <MenuItem value="manual">Manual only</MenuItem>
                        <MenuItem value="daily">Daily (yesterday at 05:20)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Switch
                      checked={settings.asrun_export.include_missed !== false}
                      onChange={(e) => updateAsRun({ include_missed: e.target.checked })}
                    />
                  }
                  label="Include missed / unplayed spots"
                />
                <FormControlLabel
                  sx={{ mb: 2 }}
                  control={
                    <Switch
                      checked={settings.asrun_export.enabled}
                      onChange={(e) => updateAsRun({ enabled: e.target.checked })}
                    />
                  }
                  label="Enable scheduled as-run uploads"
                />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Button variant="contained" component="label" startIcon={<CloudUpload />} disabled={!!busy}>
                    Upload break file
                    <input hidden type="file" accept=".m3u,.m3u8,.txt,.csv,.log" onChange={handleUpload} />
                  </Button>
                  <Button variant="outlined" startIcon={<Sync />} onClick={handleWatchImport} disabled={!!busy}>
                    Import from {method === 'dropbox' ? 'Dropbox' : method === 'local_folder' ? 'folder' : method.toUpperCase()}
                  </Button>
                  <Button variant="outlined" startIcon={<Download />} onClick={handleExportDownload} disabled={!!busy}>
                    Download spots CSV
                  </Button>
                  <Button variant="outlined" onClick={handleExportWatch} disabled={!!busy}>
                    Upload CSV
                  </Button>
                  <Button variant="outlined" startIcon={<Download />} onClick={handleAsRunDownload} disabled={!!busy}>
                    Download as-run
                  </Button>
                  <Button variant="outlined" onClick={handleAsRunUpload} disabled={!!busy}>
                    Upload as-run
                  </Button>
                </Box>

                <Typography variant="subtitle1" sx={{ color: '#a78bfa', mb: 1 }}>Recent imports</Typography>
                {imports.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No external break files imported yet.</Typography>
                ) : (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>File</TableCell>
                          <TableCell>Break</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Matched</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {imports.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.source_filename || 'upload'}</TableCell>
                            <TableCell>
                              {row.log_date
                                ? `${String(row.log_date).slice(0, 10)} ${String(row.log_hour).padStart(2, '0')}:00 · break ${row.break_no}`
                                : '—'}
                            </TableCell>
                            <TableCell><Chip size="small" color={statusColor(row.status)} label={row.status} /></TableCell>
                            <TableCell>{row.matched_count}/{row.matched_count + row.unmatched_count}</TableCell>
                            <TableCell>
                              {(row.status === 'needs_review' || row.status === 'ready') && (
                                <Button size="small" onClick={() => openImport(row.id)}>Review</Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#a78bfa' }}>
                {saving ? 'Saving…' : 'Save traffic source'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      <Dialog open={Boolean(activeImport)} onClose={() => setActiveImport(null)} maxWidth="md" fullWidth>
        <DialogTitle>Review imported break</DialogTitle>
        <DialogContent>
          {activeImport?.warning_message && (
            <Alert severity="warning" sx={{ mb: 2 }}>{activeImport.warning_message}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {activeImport?.source_filename} · {activeImport?.status}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Log line</TableCell>
                <TableCell>Match</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(activeImport?.items || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.position + 1}</TableCell>
                  <TableCell>
                    {item.raw_cart ? `${item.raw_cart} · ` : ''}
                    {item.raw_artist ? `${item.raw_artist} — ` : ''}
                    {item.raw_title}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={statusColor(item.match_status)}
                      label={
                        item.match_status === 'matched'
                          ? `${item.matched_client ? `${item.matched_client} — ` : ''}${item.matched_name || ''}`.trim()
                          : item.match_status === 'suggested'
                            ? `Suggested: ${item.suggested_name || ''}`.trim()
                            : item.match_status
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {item.match_status !== 'matched' && item.match_status !== 'skipped' && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', minWidth: 260 }}>
                        {item.suggested_spot_id && (
                          <Button size="small" onClick={() => patchItem(item.id, { action: 'accept_suggestion' })}>
                            Accept guess
                          </Button>
                        )}
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 180, flex: 1 }}
                          options={spotOptions}
                          getOptionLabel={(option) => `${option.client ? `${option.client} — ` : ''}${option.name || ''}`}
                          onOpen={() => setMatchingItemId(item.id)}
                          onInputChange={(_, value) => setSpotQuery(value)}
                          onChange={(_, value) => {
                            if (value?.id) patchItem(item.id, { action: 'match', spot_id: value.id });
                          }}
                          renderInput={(params) => <TextField {...params} label="Match to spot" />}
                        />
                        <Button size="small" color="inherit" onClick={() => patchItem(item.id, { action: 'skip' })}>
                          Skip
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveImport(null)}>Close</Button>
          {activeImport?.status !== 'applied' && activeImport?.status !== 'rejected' && (
            <>
              <Button color="inherit" onClick={() => applyOrReject('reject')}>Reject entire break</Button>
              <Button onClick={() => applyOrReject('apply', { skip_unmatched: true })}>
                Apply matched, skip the rest
              </Button>
              <Button variant="contained" onClick={() => applyOrReject('apply')} disabled={!activeImport?.can_apply}>
                Apply break
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  );
}
